import React from 'react';
import Modal from './Modal';
import {ApiError} from '../../lib/apiError';
import {formatDuration, formatDurationCompact} from '../../lib/audioMetadata';
import {formatExactDate} from '../../lib/formatDate';
import {useCountdown} from '../useCountdown';
import {ClockIcon} from '../Icons';

interface SessionLimitModalProps {
  /** The 429 `session_limit_exceeded` that was refused. Null closes the dialog. */
  error: ApiError | null;
  /** The name of the file that was refused, for the queue offer. */
  fileName: string | null;
  /** Period balance, so the copy can point at what is *not* the problem. Null when uncapped. */
  periodSecondsRemaining: number | null;
  /** When the session budget resets — already resolved from `sessionExpiresAt`/`retryAfterSeconds`. */
  releaseAt: string;
  onQueue: () => void;
  onClose: () => void;
}

/**
 * The session wall — a wait, not a paywall.
 *
 * This is deliberately **not** {@link UpgradeRequiredModal}. The 402s that modal answers are fixed
 * by spending money: the file is longer than the plan allows, or the period allowance is gone.
 * This one is not fixed by spending anything. The session throttle paces how fast the allowance can
 * be spent, every plan has one, and upgrading shortens it by exactly zero seconds. Offering a
 * purchase here would be selling a fix for something the user did not hit — the worst version of
 * this screen — so there is no plan link on it at all.
 *
 * What it offers instead is the only thing that actually helps: hold the job and start it when the
 * budget resets. The wait is real and can be hours, which is why it is worth queueing rather than
 * asking the user to come back and remember.
 *
 * It is also not the `too_many_in_flight` 429, which is three jobs already running and clears in
 * minutes. That one stays a toast — dressing a two-minute wait in this dialog would teach users to
 * dismiss the dialog that matters.
 *
 * (Time will be purchasable later. When it is, the button that appears here buys *this* wait away
 * and is still not an upgrade prompt.)
 */
const SessionLimitModal: React.FC<SessionLimitModalProps> = ({
  error,
  fileName,
  periodSecondsRemaining,
  releaseAt,
  onQueue,
  onClose,
}) => {
  const secondsLeft = useCountdown(error ? releaseAt : null);

  if (!error) {
    return null;
  }

  const isReady = secondsLeft !== null && secondsLeft <= 0;

  return (
    <Modal
      isOpen
      title="Your transcription session is full"
      subtitle={error.message}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm text-gray-400 hover:bg-gray-800
             hover:text-gray-100 transition-colors cursor-pointer"
          >
            Close
          </button>
          <button
            type="button"
            onClick={onQueue}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white
             hover:bg-indigo-500 transition-colors cursor-pointer"
          >
            {isReady ? 'Start it now' : 'Queue it until then'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-lg border border-amber-500/30
         bg-amber-500/[0.06] px-3 py-2.5">
          <ClockIcon className="h-5 w-5 shrink-0 text-amber-300" />
          <div className="min-w-0">
            <p className="text-xs text-amber-200/80">
              {isReady ? 'Your session has reset' : 'Resets in'}
            </p>
            <p className="text-lg font-medium tabular-nums text-amber-200">
              {secondsLeft !== null && !isReady ? formatDuration(secondsLeft) : 'Ready now'}
            </p>
            <p className="mt-0.5 text-xs text-amber-200/60">{formatExactDate(releaseAt)}</p>
          </div>
        </div>

        {error.duration !== undefined && error.limit != null && (
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-gray-800 bg-gray-950/50 p-3">
              <p className="text-xs text-gray-500">Your file</p>
              <p className="mt-0.5 text-lg tabular-nums text-gray-200">
                {formatDurationCompact(error.duration)}
              </p>
            </div>
            <div className="rounded-lg border border-gray-800 bg-gray-950/50 p-3">
              <p className="text-xs text-gray-500">Session budget</p>
              <p className="mt-0.5 text-lg tabular-nums text-gray-300">
                {formatDurationCompact(error.limit)}
              </p>
            </div>
          </div>
        )}

        {/* Naming the period balance is the point: without it this reads as "you are out of
            transcription time", which is a different problem with a different fix. */}
        <p className="text-sm text-gray-400">
          This is not your plan limit
          {periodSecondsRemaining !== null
            && ` — you still have ${formatDurationCompact(periodSecondsRemaining)} left this period`}
          . Sessions pace how much audio you can start at once, so upgrading would not shorten this
          wait. Nothing was charged and nothing was lost.
        </p>

        <p className="text-sm text-gray-500">
          {isReady
            ? 'Start it now, or close this and transcribe whenever you like.'
            : `Queue it and ${fileName ? `${fileName} will start` : 'it will start'} on its own `
              + 'when your session resets — you do not need to keep this page open.'}
        </p>
      </div>
    </Modal>
  );
};

export default SessionLimitModal;
